/**
 * Session Manager
 * Factory function for session management
 *
 * IMPORTANT: This module does NOT directly depend on platform.
 * Storage implementation must be injected via store parameter.
 */

import { calculateTokens } from './utils.js'
import { getUserId } from 'workers-common'
import {
  SESSION_VERSION,
  TOOL_SCHEMA_VERSION,
  TOOL_ARCHITECTURE,
} from './version.js'
import { autoMigrate } from './migration.js'

const MYAGENT_PARTICIPANT_ID = '__myagent__'

// Collection names
const NAMESPACE = 'sessions'
const COLLECTION_METADATA = 'metadata'
const COLLECTION_MESSAGES = 'llm'          // LLM track (main chat)
const COLLECTION_CONVERSATIONS = 'conv:main' // Display track (main chat)
const COLLECTION_CONV_INDEX = 'conv_index'   // Conversation index
// Configuration
export const CONFIG = {
  maxVisibleMessages: 50,
  maxConversations: 200,
  maxMessages: 500,
  maxMessageSize: 100_000, // 100KB per message content
}

/**
 * Create Session Manager API
 * Returns an object with session operations for sandbox and internal use
 *
 * @param {Object} store - Store implementation
 * @returns {Object} API functions for sandbox and internal use
 */
export function createSessionManager(store) {
  if (!store) {
    throw new Error('Store implementation is required')
  }

  // Per-user write serialization to prevent read-modify-write races
  const locks = new Map()
  const withLock = (userId, fn) => {
    const prev = locks.get(userId) || Promise.resolve()
    const next = prev.then(fn)
    const chain = next.catch(() => {})
    locks.set(userId, chain)
    chain.then(() => { if (locks.get(userId) === chain) locks.delete(userId) })
    return next
  }

  const requireAuth = (ctx) => {
    const userId = getUserId(ctx)
    if (!userId) {
      throw new Error('User not authenticated')
    }
    return userId
  }

  // Internal operations (work with userId directly)
  const loadMetadata = async (userId) => {
    let metadata = await store.get(NAMESPACE, userId, COLLECTION_METADATA)

    if (!metadata) {
      metadata = {
        userId,
        sessionId: globalThis.crypto.randomUUID(),
        version: SESSION_VERSION,
        schemaVersion: TOOL_SCHEMA_VERSION,
        toolArchitecture: TOOL_ARCHITECTURE,
        created: Date.now(),
        lastAccessed: Date.now(),
        messageCount: 0,
        tokenStats: {
          current: 0,
        },
      }
      await store.set(NAMESPACE, userId, COLLECTION_METADATA, metadata)
      return metadata
    }

    metadata.lastAccessed = Date.now()
    return metadata
  }

  const saveMetadata = async (userId, metadata) => {
    await store.set(NAMESPACE, userId, COLLECTION_METADATA, metadata)
  }

  const loadMessages = async (userId) => {
    const messages = await store.get(NAMESPACE, userId, COLLECTION_MESSAGES)
    return messages || []
  }

  const saveMessages = async (userId, messages) => {
    await store.set(NAMESPACE, userId, COLLECTION_MESSAGES, messages)
  }

  const loadConversations = async (userId) => {
    const conversations = await store.get(NAMESPACE, userId, COLLECTION_CONVERSATIONS)
    return conversations || []
  }

  const saveConversations = async (userId, conversations) => {
    await store.set(NAMESPACE, userId, COLLECTION_CONVERSATIONS, conversations)
  }

  const loadSession = async (userId, options = {}) => {
    const { addMigrationNotice = true, verbose = false } = options

    const metadata = await loadMetadata(userId)
    const messages = await loadMessages(userId)

    const sessionData = { metadata, messages }

    const migratedSession = await autoMigrate(sessionData, {
      addNotice: addMigrationNotice,
      verbose,
    })

    if (migratedSession !== sessionData) {
      console.log(`[SessionManager] Saving migrated session for user ${userId.substring(0, 8)}...`)
      await saveMetadata(userId, migratedSession.metadata)
      await saveMessages(userId, migratedSession.messages)

      const conversationMessages = migratedSession.messages.filter(
        (msg) => msg.role === 'user' || msg.role === 'assistant',
      )
      if (conversationMessages.length > 0) {
        await saveConversations(userId, conversationMessages)
      }
    }

    return migratedSession
  }

  const appendMessage = async (userId, message, options = {}) => {
    const { skipToolMessages = true } = options

    if (skipToolMessages && message.role === 'tool') {
      return
    }

    const messageWithTimestamp = {
      ...message,
      timestamp: Date.now(),
    }

    // Participant messages only go to conversations (display track), not messages (LLM track)
    if (message.role === 'participant') {
      const conversations = await loadConversations(userId)
      conversations.push(messageWithTimestamp)
      await saveConversations(userId, conversations)
      return messageWithTimestamp
    }

    const messages = await loadMessages(userId)
    messages.push(messageWithTimestamp)
    await saveMessages(userId, messages)

    if (message.role === 'user' || message.role === 'assistant') {
      const conversations = await loadConversations(userId)
      conversations.push(messageWithTimestamp)
      await saveConversations(userId, conversations)
    }

    const metadata = await loadMetadata(userId)
    metadata.messageCount = messages.length
    metadata.tokenStats.current = calculateTokens(messages)
    await saveMetadata(userId, metadata)

    return messageWithTimestamp
  }

  const getContextInternal = async (userId, options = {}) => {
    const {
      maxMessages = CONFIG.maxVisibleMessages,
      conversationsOnly = false,
    } = options

    let messages
    if (conversationsOnly) {
      messages = await loadConversations(userId)
    } else {
      messages = await loadMessages(userId)
    }
    if (messages.length > maxMessages) {
      messages = messages.slice(-maxMessages)
    }

    const metadata = await loadMetadata(userId)

    return {
      messages,
      metadata,
    }
  }

  const getMessagesForLLM = async (userId, options = {}) => {
    const {
      maxMessages = 100,
      maxToolResultLength = 2000,
      recentMessagesKeepFull = 10,  // Keep last N messages untruncated
    } = options
    const allMessages = await loadMessages(userId)

    const messages = allMessages.length > maxMessages
      ? allMessages.slice(-maxMessages)
      : allMessages

    // Process tool messages: fix broken content from old sessions + truncate older results
    const truncatedMessages = messages.map((msg, index) => {
      if (msg.role !== 'tool') return msg

      // Fix broken tool results (undefined, null, invalid JSON)
      let content = msg.content
      if (content === 'undefined' || content === undefined || content === null) {
        content = JSON.stringify({ result: null })
      } else if (typeof content === 'string') {
        try { JSON.parse(content) } catch {
          content = JSON.stringify({ result: content })
        }
      }

      const isRecent = index >= messages.length - recentMessagesKeepFull
      if (!isRecent && content.length > maxToolResultLength) {
        content = content.substring(0, maxToolResultLength) + '\n\n[... truncated for token efficiency]'
      }

      return content !== msg.content ? { ...msg, content } : msg
    })

    const systemMessages = truncatedMessages.filter((m) => m.role === 'system')
    const otherMessages = truncatedMessages.filter((m) => m.role !== 'system')

    const finalMessages = []
    if (systemMessages.length > 0) {
      finalMessages.push(systemMessages[systemMessages.length - 1])
    }
    finalMessages.push(...otherMessages)

    return finalMessages
  }

  const clearSessionInternal = async (userId) => {
    // Clear dynamic conversation collections before clearing known collections
    const index = await store.get(NAMESPACE, userId, COLLECTION_CONV_INDEX) || []
    for (const entry of index) {
      await store.remove(NAMESPACE, userId, `conv:${entry.id}`)
    }
    await store.clear(NAMESPACE, userId)
    console.log(`[SessionManager] Cleared session for user ${userId.substring(0, 8)}...`)
  }

  const getStatsInternal = async (userId) => {
    return await loadMetadata(userId)
  }

  // Return both internal methods and public API
  const api = {
    // Internal methods (for chat.js and other internal use)
    _internal: {
      loadMetadata,
      saveMetadata,
      loadMessages,
      saveMessages,
      loadSession,
      loadConversations,
      saveConversations,
      appendMessage,
      getContext: getContextInternal,
      getMessagesForLLM,
      clearSession: clearSessionInternal,
      getStats: getStatsInternal,
    },

    // Public API (for sandbox use with ctx)
    getContext: async (ctx, params = {}) => {
      const userId = requireAuth(ctx)
      const { maxMessages = 50, conversationsOnly = false } = params
      return await getContextInternal(userId, { maxMessages, conversationsOnly })
    },

    clear: async (ctx) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        await clearSessionInternal(userId)
        return { success: true, message: `Session cleared for user ${userId.substring(0, 8)}...` }
      })
    },

    clearMainChat: async (ctx) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        await store.set(NAMESPACE, userId, COLLECTION_MESSAGES, [])
        await store.set(NAMESPACE, userId, COLLECTION_CONVERSATIONS, [])
        return { success: true }
      })
    },

    getStats: async (ctx) => {
      const userId = requireAuth(ctx)
      return await getStatsInternal(userId)
    },

    getMessagesForLLM: async (ctx, params = {}) => {
      const userId = requireAuth(ctx)
      return await getMessagesForLLM(userId, params)
    },

    saveStep: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { userMessage, assistantMessage, toolResults, participantMessages, toolInteractions } = params

        // Validate message sizes at system boundary
        if (userMessage && userMessage.length > CONFIG.maxMessageSize) {
          throw new Error(`Message too large (${userMessage.length} chars, max ${CONFIG.maxMessageSize})`)
        }

        const now = Date.now()

        // Batch: load once, append all, save once
        const messages = await loadMessages(userId)
        const conversations = await loadConversations(userId)

        const addToLLM = (msg) => messages.push({ ...msg, timestamp: now })
        const addToConversations = (msg) => conversations.push({ ...msg, timestamp: now })

        if (userMessage) {
          // Dedup: skip if the last user message already matches (idempotent across iterations)
          const lastUserMsg = messages.findLast(m => m.role === 'user')
          if (!lastUserMsg || lastUserMsg.content !== userMessage) {
            const msg = { role: 'user', content: userMessage }
            addToLLM(msg)
            addToConversations(msg)
          }
        }

        if (assistantMessage) {
          // LLM track: keep as role: 'assistant' (required by LLM API)
          addToLLM({ role: 'assistant', content: assistantMessage.content, toolCalls: assistantMessage.toolCalls })
          // Display track: store as MyAgent participant message
          if (assistantMessage.content) {
            addToConversations({
              role: 'participant',
              participantId: MYAGENT_PARTICIPANT_ID,
              content: assistantMessage.content,
              metadata: { type: 'assistant_response' },
            })
          }
        }

        if (toolResults?.length > 0) {
          for (const result of toolResults) {
            addToLLM({
              role: 'tool',
              toolCallId: result.toolCallId,
              toolName: result.toolName,
              content: typeof result.content === 'string' ? result.content : JSON.stringify(result.content),
            })
          }
        }

        if (toolInteractions?.length > 0) {
          for (const ti of toolInteractions) {
            addToLLM({ role: 'assistant', content: ti.answer, toolCalls: ti.toolCalls })
            if (ti.answer) {
              addToConversations({ role: 'participant', participantId: MYAGENT_PARTICIPANT_ID, content: ti.answer, metadata: { type: 'assistant_response' } })
            }
            for (const result of ti.toolResults || []) {
              addToLLM({
                role: 'tool',
                toolCallId: result.toolCallId,
                toolName: result.toolName,
                content: typeof result.content === 'string' ? result.content : JSON.stringify(result.content),
              })
            }
          }
        }

        if (participantMessages?.length > 0) {
          for (const msg of participantMessages) {
            addToConversations(msg)
            // Participant messages with displayName go to LLM track (chat_response + async tracker progress)
            if (msg.metadata?.type === 'chat_response' || msg.metadata?.displayName) {
              const name = msg.metadata?.displayName || msg.participantId?.slice(0, 8) || 'Bot'
              const text = msg.content?.length > 2000 ? msg.content.slice(0, 2000) + '...' : msg.content
              addToLLM({ role: 'user', content: `[${name}]: ${text}` })
            }
          }
        }

        // Trim both tracks if over limit
        const trimmedMessages = messages.length > CONFIG.maxMessages
          ? messages.slice(-CONFIG.maxMessages)
          : messages
        const trimmedConversations = conversations.length > CONFIG.maxConversations
          ? conversations.slice(-CONFIG.maxConversations)
          : conversations

        await saveMessages(userId, trimmedMessages)
        await saveConversations(userId, trimmedConversations)

        const metadata = await loadMetadata(userId)
        metadata.messageCount = trimmedMessages.length
        metadata.tokenStats.current = calculateTokens(trimmedMessages)
        await saveMetadata(userId, metadata)

        return {
          success: true,
          sessionInfo: {
            messageCount: trimmedMessages.length,
            tokenUsage: {
              current: metadata.tokenStats.current,
            },
          },
        }
      })
    },

    // Participant management (stored in session metadata)
    getParticipants: async (ctx) => {
      const userId = requireAuth(ctx)
      const metadata = await loadMetadata(userId)
      return metadata.participants || []
    },

    setParticipants: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { participants } = params
      const metadata = await loadMetadata(userId)
      metadata.participants = participants
      await saveMetadata(userId, metadata)
      return metadata.participants
    },

    // --- Multi-conversation API ---

    getConversationIndex: async (ctx) => {
      const userId = requireAuth(ctx)
      return await store.get(NAMESPACE, userId, COLLECTION_CONV_INDEX) || []
    },

    createConversation: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { targetRoleId, title } = params
        const index = await store.get(NAMESPACE, userId, COLLECTION_CONV_INDEX) || []
        const entry = {
          id: globalThis.crypto.randomUUID(),
          targetRoleId,
          title: title || '',
          createdAt: Date.now(),
          updatedAt: Date.now(),
        }
        index.push(entry)
        await store.set(NAMESPACE, userId, COLLECTION_CONV_INDEX, index)
        return entry
      })
    },

    deleteConversation: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { conversationId } = params
        const index = await store.get(NAMESPACE, userId, COLLECTION_CONV_INDEX) || []
        const filtered = index.filter(e => e.id !== conversationId)
        await store.set(NAMESPACE, userId, COLLECTION_CONV_INDEX, filtered)
        await store.remove(NAMESPACE, userId, `conv:${conversationId}`)
        return { success: true }
      })
    },

    getConversationMessages: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { conversationId, maxMessages = CONFIG.maxConversations } = params
      const messages = await store.get(NAMESPACE, userId, `conv:${conversationId}`) || []
      return messages.length > maxMessages ? messages.slice(-maxMessages) : messages
    },

    saveConversationMessage: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { conversationId, messages: newMessages } = params
        const existing = await store.get(NAMESPACE, userId, `conv:${conversationId}`) || []
        const now = Date.now()
        for (const msg of newMessages) {
          existing.push({ ...msg, timestamp: msg.timestamp || now })
        }
        const trimmed = existing.length > CONFIG.maxConversations
          ? existing.slice(-CONFIG.maxConversations)
          : existing
        await store.set(NAMESPACE, userId, `conv:${conversationId}`, trimmed)

        // Update conv_index: updatedAt + auto-title from first user message
        const index = await store.get(NAMESPACE, userId, COLLECTION_CONV_INDEX) || []
        const entry = index.find(e => e.id === conversationId)
        if (entry) {
          entry.updatedAt = now
          if (!entry.title) {
            const firstUser = newMessages.find(m => m.role === 'user')
            if (firstUser) entry.title = firstUser.content.slice(0, 60)
          }
          await store.set(NAMESPACE, userId, COLLECTION_CONV_INDEX, index)
        }

        return { success: true }
      })
    },
  }

  const { _internal: _, ...sandbox } = api
  Object.defineProperty(api, 'sandbox', { value: sandbox, enumerable: false })
  return api
}
